import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartColumn,
  faCheckSquare,
  faClipboardCheck,
  faFilePdf,
  faFilter,
  faListCheck,
  faRotate,
  faSearch,
  faSpinner,
  faTicket,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../../components/Ui_Element/Breadcrumb";
import { showToast } from "../../../components/Ui_Element/CustomToast";
import { getCurrentUserIdentity } from "../../Tickets/ticketUtils";
import { TASK_STATUS_OPTIONS, formatTaskDate, getTaskPriorityClasses, getTaskStatusClasses, toDateInputValue } from "../taskUtils";
import { fetchTaskReport, fetchTaskReportStatusOptions } from "./taskReportApiService";
import { openTaskListPdfReport } from "./taskListPdfService";

const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(1);
  return toDateInputValue(date);
};

const getDefaultEndDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1, 0);
  return toDateInputValue(date);
};

const emptyReport = {
  items: [],
  statusCounts: [],
  totalTasks: 0,
};

const COLUMN_OPTIONS = [
  { key: "task", label: "Task" },
  { key: "ticket", label: "Ticket" },
  { key: "assignee", label: "Assignee" },
  { key: "priority", label: "Priority" },
  { key: "status", label: "Status" },
  { key: "activity", label: "Last Activity" },
  { key: "actions", label: "Actions" },
];

const DEFAULT_SELECTED_COLUMNS = COLUMN_OPTIONS.map((item) => item.key);

function ReportField({ label, required = false, children }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-400">
        {label} {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
    </div>
  );
}

function ColumnMultiSelect({
  options,
  selectedValues,
  onToggle,
  onSelectAll,
  onUnselectAll,
  isOpen,
  onOpenChange,
}) {
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);
  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.key))
    .map((option) => option.label)
    .join(", ");

  useEffect(() => {
    if (!isOpen) return undefined;

    const updateMenuPosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const estimatedMenuHeight = 320;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const shouldOpenUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
      const width = Math.min(Math.max(rect.width, 220), window.innerWidth - 16);
      setMenuStyle({
        position: "fixed",
        top: shouldOpenUp ? "auto" : rect.bottom + 8,
        bottom: shouldOpenUp ? window.innerHeight - rect.top + 8 : "auto",
        left: Math.max(8, rect.right - width),
        width,
        zIndex: 9999,
      });
    };

    updateMenuPosition();

    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target) && !triggerRef.current?.contains(event.target)) {
        onOpenChange(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, onOpenChange]);

  return (
    <div className="relative z-10">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => onOpenChange(!isOpen)}
        className="flex w-full items-center justify-between gap-2 rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-xs text-slate-700 dark:text-gray-300 transition hover:border-slate-300 dark:hover:border-gray-600"
      >
        <span className="min-w-0 flex-1 truncate text-left">{selectedLabels || "Select columns"}</span>
        <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
          {selectedValues.length} selected
        </span>
      </button>

      {isOpen && menuStyle ? createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 shadow-xl"
        >
          <div className="mb-2 flex items-center justify-between gap-2 border-b border-slate-100 dark:border-gray-700 px-2 pb-2">
            <button
              type="button"
              onClick={onSelectAll}
              className="rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-700 dark:text-gray-300 transition hover:bg-slate-100 dark:hover:bg-gray-700"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={onUnselectAll}
              className="rounded-lg px-2 py-1 text-[11px] font-semibold text-slate-700 dark:text-gray-300 transition hover:bg-slate-100 dark:hover:bg-gray-700"
            >
              Unselect All
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto overscroll-contain">
            {options.map((option) => {
              const checked = selectedValues.includes(option.key);

              return (
                <label
                  key={option.key}
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-slate-700 dark:text-gray-300 transition hover:bg-slate-50 dark:hover:bg-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(option.key)}
                    disabled={selectedValues.length === 1 && checked}
                    className="h-4 w-4 shrink-0 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                  />
                  <span className="min-w-0 flex-1 break-words">{option.label}</span>
                </label>
              );
            })}
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  );
}

export default function TaskReportPage() {
  const location = useLocation();
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedUserId = searchParams.get("userId") || identity.userId || "";

  const [statusOptions, setStatusOptions] = useState(["All", ...TASK_STATUS_OPTIONS]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(emptyReport);
  const [selectedColumns, setSelectedColumns] = useState(DEFAULT_SELECTED_COLUMNS);
  const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
  const [filters, setFilters] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate(),
    status: "All",
  });

  useEffect(() => {
    let cancelled = false;

    const loadStatusOptions = async () => {
      setFiltersLoading(true);

      try {
        const statuses = await fetchTaskReportStatusOptions();
        if (!cancelled && statuses.length) {
          setStatusOptions(["All", ...statuses]);
        }
      } catch {
        if (!cancelled) {
          setStatusOptions(["All", ...TASK_STATUS_OPTIONS]);
        }
      } finally {
        if (!cancelled) {
          setFiltersLoading(false);
        }
      }
    };

    loadStatusOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  const statusTotalsMap = useMemo(() => {
    return report.statusCounts.reduce((result, item) => {
      result[item.status] = Number(item.count || 0);
      return result;
    }, {});
  }, [report.statusCounts]);

  const statusCards = useMemo(() => {
    return ["Pending", "In Progress", "Waiting", "Completed", "Cancelled"].map((status) => ({
      status,
      count: statusTotalsMap[status] || 0,
    }));
  }, [statusTotalsMap]);

  const reportCompanyInfo = useMemo(() => ({
    name: "BanglaFire CRM",
    code: "Support Desk",
    email: "support@banglafire.com",
    phone: "",
    website: "www.banglafire.com",
    address: "BanglaFire CRM Support Desk",
  }), []);

  const reportFilterSummary = useMemo(() => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
    status: filters.status === "All" ? "All Statuses" : filters.status,
    requestedUserId: requestedUserId || "",
    requestedUserName: identity.fullName || identity.email || "Current user",
  }), [filters.endDate, filters.startDate, filters.status, identity.email, identity.fullName, requestedUserId]);

  const validateFilters = () => {
    if (!filters.startDate || !filters.endDate) {
      throw new Error("Start date and end date are required.");
    }

    if (filters.endDate < filters.startDate) {
      throw new Error("End date must be on or after start date.");
    }
  };

  const handleSearch = async () => {
    try {
      validateFilters();
    } catch (validationError) {
      const message = validationError?.message || "Please review the report filters.";
      setError(message);
      showToast.error(message);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await fetchTaskReport({
        userId: requestedUserId,
        startDate: filters.startDate,
        endDate: filters.endDate,
        status: filters.status === "All" ? "" : filters.status,
      });

      setReport(result);
      setSearched(true);
    } catch (requestError) {
      const message = requestError?.message || "Task report could not be loaded.";
      setError(message);
      setReport(emptyReport);
      setSearched(true);
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);

    try {
      await openTaskListPdfReport({
        companyInfo: reportCompanyInfo,
        filters: reportFilterSummary,
        report,
        selectedColumns,
      });
    } catch (exportError) {
      const message = exportError?.message || "PDF report could not be generated.";
      showToast.error(message);
    } finally {
      setExportingPdf(false);
    }
  };

  const handleColumnToggle = (columnKey) => {
    setSelectedColumns((current) => {
      if (current.includes(columnKey)) {
        if (current.length === 1) {
          return current;
        }

        return current.filter((item) => item !== columnKey);
      }

      return [...current, columnKey];
    });
  };

  const handleSelectAllColumns = () => {
    setSelectedColumns(DEFAULT_SELECTED_COLUMNS);
    setIsColumnMenuOpen(false);
  };

  const handleUnselectAllColumns = () => {
    setSelectedColumns([DEFAULT_SELECTED_COLUMNS[0]]);
    setIsColumnMenuOpen(false);
  };

  const isColumnVisible = (columnKey) => selectedColumns.includes(columnKey);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0F111A] p-4 md:p-6 transition-colors duration-300">
      <Breadcrumb pageName="Task Report" currentPage="Task Report" />

      <div className="mt-4 overflow-hidden rounded-3xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-[#161B22] shadow-sm">
        <div className="relative overflow-hidden px-5 py-6 text-white md:px-7 md:py-8"
          style={{ background: "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)" }}>
          <div className="pointer-events-none absolute -right-10 -top-10 h-52 w-52 rounded-full bg-white/5 blur-3xl" />
          <div className="pointer-events-none absolute bottom-0 left-1/4 h-28 w-28 rounded-full blur-3xl" style={{ background: "rgba(255,160,100,0.12)" }} />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-orange-100">
                <FontAwesomeIcon icon={faChartColumn} />
                Support Task Reporting
              </div>
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">My task report by date and status</h1>
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-orange-100">
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{identity.fullName || identity.email || "Current user"}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">User ID: {requestedUserId || "Unavailable"}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">Filter tasks by activity date and current status</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSearch}
              disabled={loading || filtersLoading}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold shadow-lg transition hover:bg-orange-50 active:scale-95 disabled:cursor-not-allowed disabled:opacity-70"
              style={{ color: "#C04C2A" }}
            >
              <FontAwesomeIcon icon={loading ? faSpinner : faSearch} spin={loading} />
              {loading ? "Loading..." : "Run Report"}
            </button>
          </div>
        </div>

        <div className="space-y-6 p-5 md:p-6">
          <div className="rounded-3xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-[#161B22] p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-gray-200">Filters</h2>
              </div>
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-white" style={{ background: "linear-gradient(135deg, #C04C2A, #4A1B13)" }}>
                <FontAwesomeIcon icon={faFilter} />
              </div>
            </div>

            {error ? <div className="mt-4 rounded-2xl border border-rose-200 dark:border-red-800 bg-rose-50 dark:bg-red-900/20 px-4 py-3 text-sm text-rose-700 dark:text-red-400">{error}</div> : null}

            <div className="mt-5 grid gap-4 lg:grid-cols-4">
              <ReportField label="Start Date" required>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(event) => setFilters((current) => ({ ...current, startDate: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-white px-4 py-3 text-sm outline-none transition focus:border-slate-400 dark:focus:border-gray-500"
                />
              </ReportField>

              <ReportField label="End Date" required>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(event) => setFilters((current) => ({ ...current, endDate: event.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-white px-4 py-3 text-sm outline-none transition focus:border-slate-400 dark:focus:border-gray-500"
                />
              </ReportField>

              <ReportField label="Status">
                <select
                  value={filters.status}
                  onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
                  disabled={filtersLoading}
                  className="w-full rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-900 dark:text-white px-4 py-3 text-sm outline-none transition focus:border-slate-400 dark:focus:border-gray-500 disabled:bg-slate-50 dark:disabled:bg-gray-900"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status === "All" ? "All Status" : status}
                    </option>
                  ))}
                </select>
              </ReportField>

              <div className="flex items-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setFilters({
                      startDate: getDefaultStartDate(),
                      endDate: getDefaultEndDate(),
                      status: "All",
                    });
                    setReport(emptyReport);
                    setError("");
                    setSearched(false);
                  }}
                  className="flex-1 rounded-full border border-slate-200 dark:border-gray-700 px-4 py-3 text-sm font-semibold text-slate-700 dark:text-gray-300 transition hover:bg-slate-50 dark:hover:bg-gray-800"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading || filtersLoading}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-bold text-white transition hover:opacity-90 active:scale-95 disabled:cursor-not-allowed disabled:opacity-70"
                  style={{ background: "linear-gradient(135deg, #C04C2A, #4A1B13)" }}
                >
                  <FontAwesomeIcon icon={loading ? faSpinner : faSearch} spin={loading} />
                  Search
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-[#161B22] p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-gray-200">Status Snapshot</h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">Quick count of tasks matching the selected date range and status filter.</p>
              </div>
              <button
                type="button"
                onClick={handleSearch}
                disabled={loading || filtersLoading}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 px-4 py-2.5 text-sm font-semibold text-slate-700 dark:text-gray-300 transition hover:bg-slate-100 dark:hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                Refresh Result
              </button>
              <button
                type="button"
                onClick={handleExportPdf}
                disabled={loading || filtersLoading || exportingPdf}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
                style={{ background: "linear-gradient(135deg, #C04C2A, #4A1B13)" }}
              >
                <FontAwesomeIcon icon={exportingPdf ? faSpinner : faFilePdf} spin={exportingPdf} />
                {exportingPdf ? "Preparing PDF..." : "PDF Report"}
              </button>
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-3 xl:grid-cols-6">
              <div className="rounded-2xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/50 px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-gray-400">Total Tasks</div>
                <div className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">{report.totalTasks || 0}</div>
              </div>
              {statusCards.map((item) => (
                <div key={item.status} className="rounded-2xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800/50 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-gray-400">{item.status}</div>
                  <div className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">{item.count}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-visible rounded-3xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-[#161B22] shadow-sm">
            <div className="border-b border-slate-200 dark:border-gray-800 px-5 py-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-gray-200">Task Report Rows</h2>
                <div className="flex items-center justify-end gap-2 lg:w-[280px]">
                  <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white" style={{ background: "linear-gradient(135deg, #C04C2A, #4A1B13)" }}>
                    <FontAwesomeIcon icon={faCheckSquare} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <ColumnMultiSelect
                      options={COLUMN_OPTIONS}
                      selectedValues={selectedColumns}
                      onToggle={handleColumnToggle}
                      onSelectAll={handleSelectAllColumns}
                      onUnselectAll={handleUnselectAllColumns}
                      isOpen={isColumnMenuOpen}
                      onOpenChange={setIsColumnMenuOpen}
                    />
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="px-6 py-16 text-center text-sm text-slate-500 dark:text-gray-400">
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Loading report rows...
              </div>
            ) : !searched ? (
              <div className="px-6 py-16 text-center text-sm text-slate-500 dark:text-gray-400">
                Run the report to see task rows.
              </div>
            ) : report.items.length ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-gray-800/50 text-left">
                    <tr>
                      {isColumnVisible("task") ? <th className="px-5 py-3 font-semibold text-slate-600 dark:text-gray-400">Task</th> : null}
                      {isColumnVisible("ticket") ? <th className="px-5 py-3 font-semibold text-slate-600 dark:text-gray-400">Ticket</th> : null}
                      {isColumnVisible("assignee") ? <th className="px-5 py-3 font-semibold text-slate-600 dark:text-gray-400">Assignee</th> : null}
                      {isColumnVisible("priority") ? <th className="px-5 py-3 font-semibold text-slate-600 dark:text-gray-400">Priority</th> : null}
                      {isColumnVisible("status") ? <th className="px-5 py-3 font-semibold text-slate-600 dark:text-gray-400">Status</th> : null}
                      {isColumnVisible("activity") ? <th className="px-5 py-3 font-semibold text-slate-600 dark:text-gray-400">Last Activity</th> : null}
                      {isColumnVisible("actions") ? <th className="px-5 py-3 text-right font-semibold text-slate-600 dark:text-gray-400">Actions</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.map((item) => (
                      <tr key={item.taskId} className="border-t border-slate-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        {isColumnVisible("task") ? <td className="px-5 py-3 align-top">
                          <div className="font-semibold text-slate-900 dark:text-gray-200">{item.title}</div>
                          <div className="mt-1 max-w-md text-sm text-slate-500 dark:text-gray-400">{item.description || "No description"}</div>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-400 dark:text-gray-500">
                            <span>Created by {item.createdByName || "Unknown"}</span>
                            <span>Created {formatTaskDate(item.createdAt, true)}</span>
                            <span>Due {formatTaskDate(item.dueDate)}</span>
                          </div>
                        </td> : null}
                        {isColumnVisible("ticket") ? <td className="px-5 py-3 align-top">
                          {item.ticketId ? (
                            <Link
                              to={`/tickets/${item.ticketId}`}
                              state={{ backTo: `${location.pathname}${location.search}`, viewOnly: true }}
                              className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-gray-300 transition hover:bg-slate-100 dark:hover:bg-gray-700"
                            >
                              <FontAwesomeIcon icon={faTicket} />
                              {item.ticketNumber || "Open Ticket"}
                            </Link>
                          ) : (
                            <span className="text-slate-400 dark:text-gray-500">Manual task</span>
                          )}
                          <div className="mt-2 text-sm text-slate-500 dark:text-gray-400">{item.ticketSubject || "No linked ticket subject"}</div>
                        </td> : null}
                        {isColumnVisible("assignee") ? <td className="px-5 py-3 align-top">
                          <div className="font-medium text-slate-900 dark:text-gray-200">{item.assignedEmployeeName || "Unassigned"}</div>
                          <div className="mt-1 text-xs text-slate-500 dark:text-gray-400">{item.assignedEmployeeEmail || item.assignedUserId || "No email"}</div>
                        </td> : null}
                        {isColumnVisible("priority") ? <td className="px-5 py-3 align-top">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getTaskPriorityClasses(item.priority)}`}>
                            {item.priority}
                          </span>
                        </td> : null}
                        {isColumnVisible("status") ? <td className="px-5 py-3 align-top">
                          <div className="space-y-2">
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getTaskStatusClasses(item.status)}`}>
                              {item.status}
                            </span>
                            <div className="text-xs text-slate-500 dark:text-gray-400">Progress: {Number(item.progressPercent || 0)}%</div>
                          </div>
                        </td> : null}
                        {isColumnVisible("activity") ? <td className="px-5 py-3 align-top">
                          <div className="font-medium text-slate-900 dark:text-gray-200">{formatTaskDate(item.lastActivityAt || item.updatedAt, true)}</div>
                          <div className="mt-1 text-xs text-slate-500 dark:text-gray-400">{item.lastActionLabel} by {item.lastActorName || "Unknown"}</div>
                          {item.lastNote ? <div className="mt-2 text-xs text-slate-500 dark:text-gray-400">{item.lastNote}</div> : null}
                        </td> : null}
                        {isColumnVisible("actions") ? <td className="px-5 py-3 align-top">
                          <div className="flex justify-end gap-2">
                            {item.ticketId ? (
                              <Link
                                to={`/tickets/${item.ticketId}`}
                                state={{ backTo: `${location.pathname}${location.search}`, viewOnly: true }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-gray-300 transition hover:bg-slate-50 dark:hover:bg-gray-700"
                              >
                                Ticket
                              </Link>
                            ) : null}
                            <Link
                              to={requestedUserId ? `/my-tasks?userId=${encodeURIComponent(requestedUserId)}` : "/my-tasks"}
                              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                            >
                              <FontAwesomeIcon icon={faClipboardCheck} />
                              Task Desk
                            </Link>
                          </div>
                        </td> : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-6 py-16 text-center text-sm text-slate-500 dark:text-gray-400">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl text-white" style={{ background: "linear-gradient(135deg, #C04C2A, #4A1B13)" }}>
                  <FontAwesomeIcon icon={faListCheck} />
                </div>
                No tasks matched the selected filters.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
